import type { ColumnFiltersState } from '@tanstack/react-table';
import { createMutationAtom } from 'atoms/create-resource-atom';
import searchAtomPair, { SearchParams } from 'atoms/search-atom';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { debounce, isNil, snakeCase } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DiFormat,
  ForecastDurationOption,
  ProductionSearchParams,
} from 'types/api-payloads';
import {
  BestFitUnitResponse,
  BulkForecastMetricData,
  DcaScenariosResponse,
  DeclineCurveAnalysis,
  type ProductionDataSource,
  VolumeMetric,
  WellIntersectionAnalysisResponse,
  WellPDPDeclineMetricData,
  WellLandingAnalysisInstance,
  WellProductionForecastResponse,
  WellProductionRecordResponse,
  WellsResponse,
} from 'types/api-responses';
import { isDailyProductionSource } from 'components/production-source-dropdown';
import type { VolumeDisplayMode } from './decline-curve-analysis/chart-actions';
import {
  DAILY_GRANULARITY_WITH_SERIES,
  MONTHLY_GRANULARITY,
  NO_VERSIONS,
} from './production-request-params';
import type {
  StimulationResponse,
  StimulationRow,
} from 'types/well-stimulations';
import { formatMutationResource } from 'utils/misc';
import request from 'lib/request';
import {
  convertToHyperbolicErrorAtom,
  gasModelFormAtom,
  gasModelFormValuesAtom,
  isGasModelValidAtom,
  isOilModelValidAtom,
  isWaterModelValidAtom,
  oilModelFormAtom,
  oilModelFormValuesAtom,
  primaryDCAModelAtom,
  regressionDataAtom,
  selectedProductionSourceAtom,
  useProductionForecastsVersionsAtom,
  useProductionRecordsVersionsAtom,
  triggerEditSourceRefreshAtom,
  useWellLandingAnalysisPreviewAtom,
  waterModelFormAtom,
  waterModelFormValuesAtom,
  wellLandingAnalysisMutationAtom,
} from './atoms';
import {
  FailedApiResponse,
  LandingAnalysisBannerResult,
  LandingBannerType,
  ProductionRecordSearchParams,
  UseLandingAnalysisBannerParams,
  VolumeModelForm,
  VolumeModelFormAtoms,
  WellVersions,
} from './types';
import {
  generateVersionLabel,
  getValidationRange,
  hasDeclineModelChanged,
  HOTKEY_CONFIG,
  HOTKEY_CONFIG_BY_MAPPING,
} from './utils';

import { AtomField } from 'atoms/form-atoms';
import { createResourceAtom } from 'lib/atoms';
import { useInsightsHotkeys } from 'lib/hooks/useInsightsHotkeys';
import { useHotkeys, useHotkeysContext } from 'react-hotkeys-hook';
import {
  parseMomentUtc,
  parseUTCdateTime,
  SERVER_DATE_FORMAT,
} from 'utils/dates';

import { HOTKEY_SCOPES } from 'lib/constants';
import { dcaHotkeysModeAtom } from './decline-curve-analysis/hotkeys-mode/atoms';
// eslint-disable-next-line camelcase
import { unstable_batchedUpdates } from 'react-dom';
import { MineralType } from 'types/minerals';

export const STREAM_ORDER: HOTKEY_SCOPES[] = [
  'DCA_INPUT_FORM_OIL',
  'DCA_INPUT_FORM_GAS',
  'DCA_INPUT_FORM_WATER',
];

// The well-analysis hrefs off the well's own resources. Memoised on the well's UWI rather
// than latched in a ref: on the Well page the well never changes so either works, but the
// workspace steps this through its wells, and a ref set once left Production Details
// reading the first well no matter which one the stepper had moved to.
export const useWellAnalysisHrefs = (well?: WellsResponse) =>
  useMemo(() => {
    if (!well) return undefined;

    return {
      uwi: well.uwi,
      productionRecordsHref: well.resources.queries.productionRecords.href,
      productionRecordsVersionsHref:
        well.resources.queries.productionRecordsVersions.href,
      productionForecastsHref: well.resources.queries.productionForecasts.href,
      productionForecastsVersionsHref:
        well.resources.queries.productionForecastsVersions.href,
      dailyProductionHref: well.resources.queries.dailyProduction?.href,
      dailyProductionVersionsHref:
        well.resources.queries.dailyProductionVersions?.href,
      productionDataSources: well.productionDataSources,
    };
    // Keyed on the well: the same well gives the same hrefs, a new well gives new ones.
  }, [well]);

/**
 * A well's production versions from one source, newest first, each with the
 * label the version dropdowns show. The list is fetched when first used.
 *
 * @param {string} recordVersionHref - The source's versions endpoint.
 * @param {string} uwi - The well.
 * @return {object} The versions, their loading and error state, and the setter that fetches them again.
 */
export const useRecordVersions = (recordVersionHref: string, uwi: string) => {
  const { searchAtom: recordVersionAtom } = useProductionRecordsVersionsAtom(
    recordVersionHref,
    uwi
  );

  const [recordVersionResponse, setRecordVersionResponse] =
    useAtom(recordVersionAtom);

  // Built once per response, so a selection drawn from it keeps its identity
  // between renders.
  const recordVersions = useMemo(
    () =>
      recordVersionResponse.data?.results?.map((version) => ({
        ...version,
        label: generateVersionLabel(
          version.versionName,
          version.versionId,
          version.beginTime,
          version.endTime,
          false
        ),
      })) ?? NO_VERSIONS,
    [recordVersionResponse.data]
  );

  return {
    recordVersions,
    setRecordVersionResponse,
    isRecordVersionsLoading: recordVersionResponse.loading,
    recordVersionsError: recordVersionResponse.error,
  };
};

export const useProductionVersions = (
  recordVersionHref: string,
  forecastVersionHref: string,
  insightForecastVersionHref: string,
  uwi: string
) => {
  const {
    recordVersions,
    setRecordVersionResponse,
    isRecordVersionsLoading,
    recordVersionsError,
  } = useRecordVersions(recordVersionHref, uwi);

  const { searchAtom: forecastVersionAtom } =
    useProductionForecastsVersionsAtom(forecastVersionHref, uwi, true);

  const { searchAtom: insightForecastVersionAtom } =
    useProductionForecastsVersionsAtom(insightForecastVersionHref, uwi);

  const [forecastVersionResponse, setForecastVersionResponse] =
    useAtom(forecastVersionAtom);

  const [insightForecastVersionResponse, setInsightForecastVersionResponse] =
    useAtom(insightForecastVersionAtom);

  const {
    data: forecastVersionsResponse,
    error: forecastVersionsError,
    loading: isForecastVersionsLoading,
  } = forecastVersionResponse;

  const {
    data: insightForecastVersionsResponse,
    error: insightForecastVersionsError,
    loading: isInsightForecastVersionsLoading,
  } = insightForecastVersionResponse;

  const forecastVersions = forecastVersionsResponse?.results?.map((version) => {
    return {
      ...version,
      label:
        generateVersionLabel(
          version.versionName,
          version.versionId,
          version.beginTime,
          null,
          true
        ) + '| Legacy',
    };
  });

  const insightForecastVersions = insightForecastVersionsResponse?.results?.map(
    (version, index) => {
      return {
        ...version,
        label:
          generateVersionLabel(
            version.versionName,
            index === 0 ? 'current' : version.versionId,
            version.beginTime,
            null,
            true
          ) + '| Insights',
      };
    }
  );
  return {
    recordVersions,
    forecastVersions: forecastVersions ?? [],
    insightForecastVersions: insightForecastVersions ?? [],
    setRecordVersionResponse,
    setForecastVersionResponse,
    setInsightForecastVersionResponse,
    isRecordVersionsLoading,
    isForecastVersionsLoading,
    isInsightForecastVersionsLoading,
    recordVersionsError,
    forecastVersionsError,
    insightForecastVersionsError,
  };
};

/**
 * The DCA editor's production, read from the source selected in its
 * configuration panel (selectedProductionSourceAtom).
 *
 * Monthly production is the IHS records, or daily production summed by month.
 * With daily production on the Daily display, the charts draw it one row per
 * day instead, with each day's running totals, ratios and material balance
 * time. Switching the source resets the display to that
 * source's default: Daily for daily production, Monthly for IHS.
 *
 * @param {object} options - The well, its production endpoints and the selected versions.
 * @param {string} options.uwi - The well.
 * @param {string} options.recordHref - IHS production.
 * @param {string | undefined} options.dailyRecordHref - Daily production; without it, the source is always IHS.
 * @param {WellVersions} options.selectedRecordVersions - The production versions to read.
 * @return {object} The production, the display state, the source it is read from, and the eval_volumes param naming that source.
 */
export const useDcaEditorProduction = ({
  uwi,
  recordHref,
  dailyRecordHref,
  selectedRecordVersions,
}: {
  uwi: string;
  recordHref: string;
  dailyRecordHref: string | undefined;
  selectedRecordVersions: WellVersions;
}) => {
  const productionSource = useAtomValue(selectedProductionSourceAtom);
  const dailyHref = isDailyProductionSource(productionSource)
    ? dailyRecordHref
    : undefined;
  const isDailySource = Boolean(dailyHref);
  const shownSource = isDailySource ? productionSource : 'ihs';

  const productionSourceParam = useMemo(
    () => toProductionSourceParam(shownSource),
    [shownSource]
  );
  const [volumeDisplayMode, setVolumeDisplayMode] =
    useVolumeDisplayMode(isDailySource);
  const showDailyRows = isDailySource && volumeDisplayMode === 'daily';

  const monthly = useProduction<
    WellProductionRecordResponse,
    ProductionRecordSearchParams
  >(
    dailyHref ?? recordHref,
    uwi,
    selectedRecordVersions,
    isDailySource ? 'dailyProduction' : 'productionVolumes',
    0,
    isDailySource ? MONTHLY_GRANULARITY : undefined
  );

  const daily = useProduction<
    WellProductionRecordResponse,
    ProductionRecordSearchParams
  >(
    dailyHref ?? recordHref,
    uwi,
    showDailyRows ? selectedRecordVersions : NO_VERSIONS,
    'dailyProduction',
    0,
    DAILY_GRANULARITY_WITH_SERIES
  );

  return {
    isDailySource,
    /** The source the production is read from, IHS included. */
    productionSource: shownSource,
    productionSourceParam,
    volumeDisplayMode,
    setVolumeDisplayMode,
    showDailyRows,
    /** Monthly production for the first selected version. */
    productionVolume: monthly.data[0],
    /** What the charts draw: daily rows (with their running totals, ratios and material balance time) or monthly production. */
    chartProductionVolume: showDailyRows ? daily.data[0] : monthly.data[0],
    isProductionLoading:
      monthly.isLoading || (showDailyRows && daily.isLoading),
  };
};

/**
 * The volume display a production source starts with.
 *
 * @param {boolean} isDailySource - Whether the source is daily production.
 * @return {VolumeDisplayMode} Daily for daily production, Monthly otherwise.
 */
const defaultVolumeDisplayMode = (isDailySource: boolean): VolumeDisplayMode =>
  isDailySource ? 'daily' : 'monthly';

/**
 * The volume display (Monthly or Daily) for production that may include daily
 * production. It starts on Daily with daily production and Monthly without,
 * and goes back to that default whenever daily production is added or removed.
 *
 * @param {boolean} hasDailyProduction - Whether the production shown includes daily production.
 * @return {[VolumeDisplayMode, (mode: VolumeDisplayMode) => void]} The display and its setter.
 */
export const useVolumeDisplayMode = (hasDailyProduction: boolean) => {
  const [volumeDisplayMode, setVolumeDisplayMode] = useState<VolumeDisplayMode>(
    () => defaultVolumeDisplayMode(hasDailyProduction)
  );
  const [modeHasDailyProduction, setModeHasDailyProduction] =
    useState(hasDailyProduction);
  if (modeHasDailyProduction !== hasDailyProduction) {
    setModeHasDailyProduction(hasDailyProduction);
    setVolumeDisplayMode(defaultVolumeDisplayMode(hasDailyProduction));
  }
  return [volumeDisplayMode, setVolumeDisplayMode] as const;
};

/**
 * The eval_volumes param that names a production source. eval_volumes reads
 * IHS production unless told otherwise, so only a daily source is named.
 *
 * @param {ProductionDataSource} source - The source to read production from.
 * @return {{ productionSource?: ProductionDataSource }} The param, empty for IHS.
 */
export const toProductionSourceParam = (
  source: ProductionDataSource
): { productionSource?: ProductionDataSource } =>
  isDailyProductionSource(source) ? { productionSource: source } : {};

export const useProduction = <
  T extends WellProductionRecordResponse | WellProductionForecastResponse,
  P extends SearchParams &
    Partial<{
      [
        key in 'productionVolumes' | 'dailyProduction' | 'pdpForecast'
      ]: Array<ProductionSearchParams>;
    }>,
>(
  href: string,
  uwi: string,
  selectedVersions: WellVersions,
  key: 'productionVolumes' | 'dailyProduction' | 'pdpForecast',
  debounceTime: number = 800,
  /** Sent with every request, e.g. a `responseConfig`. Keep it stable between renders. */
  extraParams?: Partial<P>
) => {
  const { searchAtom: productionSearchAtom } = useMemo(() => {
    return searchAtomPair<T, P>({ href });
  }, [href]);

  const [productionResponse, setProductionResponse] =
    useAtom(productionSearchAtom);

  const debouncedSetProductionResponse = useMemo(() => {
    return debounce(setProductionResponse, debounceTime);
  }, [setProductionResponse, debounceTime]);

  useEffect(() => {
    if (selectedVersions.length > 0) {
      debouncedSetProductionResponse((prev) => {
        return {
          ...(prev as P),
          ...extraParams,
          [key]: [
            {
              uwis: [uwi],
              versionIds: selectedVersions.map((version) => version.versionId),
            },
          ],
        };
      });
    } else {
      // Cancel pending requests and clear data when no versions selected
      debouncedSetProductionResponse.cancel();
      // setProductionResponse(() => undefined);
    }
  }, [
    selectedVersions,
    uwi,
    key,
    extraParams,
    debouncedSetProductionResponse,
    // setProductionResponse,
  ]);

  const sortedProductionData = productionResponse.data?.results[0][uwi]?.sort(
    (a, b) => {
      const aVersionId =
        'productionVolumeVersionId' in a
          ? a.productionVolumeVersionId
          : a.pdpForecastVersionId;
      const bVersionId =
        'productionVolumeVersionId' in b
          ? b.productionVolumeVersionId
          : b.pdpForecastVersionId;
      const aIndex = selectedVersions.findIndex(
        (v) => v.versionId === aVersionId
      );
      const bIndex = selectedVersions.findIndex(
        (v) => v.versionId === bVersionId
      );
      return aIndex - bIndex;
    }
  ) as T[string];
  return {
    data: sortedProductionData ?? [],
    isLoading: productionResponse.loading,
    error: productionResponse.error,
  };
};

export const useVolumeMetric = () => {
  const [resetKey, setResetKey] = useState(0);
  const volumeMetricAtom = useMemo(() => {
    return createMutationAtom<VolumeMetric, 'volumeMetric'>();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);
  const [volumeMetricResponse, setVolumeMetricResponse] =
    useAtom(volumeMetricAtom);

  const { data, loading, error } = formatMutationResource(volumeMetricResponse);

  const resetVolumeMetric = useCallback(() => {
    setResetKey((prev) => prev + 1);
  }, []);

  return {
    data,
    loading,
    error,
    setVolumeMetricResponse,
    resetVolumeMetric,
  };
};

export const useBulkForecastMetric = () => {
  const bulkForecastMetricAtom = useMemo(() => {
    return createMutationAtom<BulkForecastMetricData, 'bulkForecastMetric'>();
  }, []);
  const [bulkForecastMetricResponse, setBulkForecastMetricResponse] = useAtom(
    bulkForecastMetricAtom
  );

  const { data, loading, error } = formatMutationResource(
    bulkForecastMetricResponse
  );

  return {
    data,
    loading,
    error,
    setBulkForecastMetricResponse,
  };
};

/**
 * Reads the metrics for each of a well's forecasts, with one eval_volumes
 * request per forecast. The endpoint returns a single forecast for a well per
 * request, so asking for several at once loses all but one of them.
 *
 * The requests wait 800ms after the last change to `queries`, so picking
 * several versions in a row sends one set of requests.
 *
 * @param {string} uwi - The well.
 * @param {object[]} queries - One eval_volumes query per forecast. Memoize it, since a new list sends the requests again.
 * @return {{ entries: WellPDPDeclineMetricData[], loading: boolean }} What the requests returned for the well, and whether they are still running.
 */
export const useForecastMetrics = (uwi: string, queries: object[]) => {
  const [entries, setEntries] = useState<WellPDPDeclineMetricData[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (queries.length === 0) {
      setEntries([]);
      return;
    }

    const abortController = new AbortController();
    const timer = setTimeout(async () => {
      setLoading(true);

      const responses = await Promise.all(
        queries.map((query) =>
          request.post<VolumeMetric, unknown>(
            '/queries/reservoir/eval_volumes',
            { query },
            abortController
          )
        )
      );
      if (abortController.signal.aborted) return;

      setEntries(
        responses.flatMap((response) => {
          if (response.type !== 'success') return [];

          return response.data.pdpDeclines?.[uwi] ?? [];
        })
      );
      setLoading(false);
    }, 800);

    return () => {
      clearTimeout(timer);
      abortController.abort();
      setLoading(false);
    };
  }, [uwi, queries]);

  return { entries, loading };
};

export const useDeclineCurveAnalysis = (uwi: string, versionId: string) => {
  const [resetKey, setResetKey] = useState(0);
  const fallbackAtom = useMemo(
    () =>
      atom({ value: undefined, isValid: true, isDirty: false, error: null }),
    []
  );
  const [regressionData, setRegressionData] = useAtom(regressionDataAtom);
  const setConvertToHyperbolicError = useSetAtom(convertToHyperbolicErrorAtom);
  const oilForm = useAtomValue(oilModelFormAtom);
  const gasForm = useAtomValue(gasModelFormAtom);
  const waterForm = useAtomValue(waterModelFormAtom);
  const isOilModelValid = useAtomValue(isOilModelValidAtom);
  const isGasModelValid = useAtomValue(isGasModelValidAtom);
  const isWaterModelValid = useAtomValue(isWaterModelValidAtom);
  const prevModelFormValues = useRef<{
    oil: VolumeModelForm | null;
    gas: VolumeModelForm | null;
    water: VolumeModelForm | null;
  }>({ oil: null, gas: null, water: null });

  const declineCurveAnalysisAtom = useMemo(() => {
    return createMutationAtom<DeclineCurveAnalysis, 'declineCurveAnalysis'>();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);
  const [declineCurveAnalysisResponse, setDeclineCurveAnalysisResponse] =
    useAtom(declineCurveAnalysisAtom);

  const debouncedSetDeclineCurveAnalysisResponse = useMemo(() => {
    return debounce(setDeclineCurveAnalysisResponse, 300);
  }, [setDeclineCurveAnalysisResponse]);

  const oilFormValues = {
    startDate: useAtomValue(oilForm.startDateAtomField),
    qi: useAtomValue(oilForm.qiAtomField),
    di: useAtomValue(oilForm.diAtomField),
    declineModel: useAtomValue(oilForm.declineModelAtomField),
    bFactor: useAtomValue(oilForm.bFactorAtomField ?? fallbackAtom),
    dlim: useAtomValue(oilForm.dlimAtomField ?? fallbackAtom),
    forecastDuration: useAtomValue(oilForm.forecastDuration),
    qiFormat: useAtomValue(oilForm.qiFormat),
    diFormat: useAtomValue(oilForm.diFormat),
    rampupModel: useAtomValue(oilForm.rampupModelAtomField),
    qiRampup: useAtomValue(oilForm.qiRampupAtomField ?? fallbackAtom),
    rampupDuration: useAtomValue(
      oilForm.rampupDurationAtomField ?? fallbackAtom
    ),
    n: useAtomValue(oilForm.nAtomField ?? fallbackAtom),
    ri: useAtomValue(oilForm.riAtomField ?? fallbackAtom),
    tElf: useAtomValue(oilForm.tElfAtomField ?? fallbackAtom),
    m: useAtomValue(oilForm.mAtomField ?? fallbackAtom),
    forecastDurationOption: useAtomValue(oilForm.forecastDurationOption),
  };

  const gasFormValues = {
    startDate: useAtomValue(gasForm.startDateAtomField),
    qi: useAtomValue(gasForm.qiAtomField),
    di: useAtomValue(gasForm.diAtomField),
    declineModel: useAtomValue(gasForm.declineModelAtomField),
    bFactor: useAtomValue(gasForm.bFactorAtomField ?? fallbackAtom),
    dlim: useAtomValue(gasForm.dlimAtomField ?? fallbackAtom),
    forecastDuration: useAtomValue(gasForm.forecastDuration),
    qiFormat: useAtomValue(gasForm.qiFormat),
    diFormat: useAtomValue(gasForm.diFormat),
    rampupModel: useAtomValue(gasForm.rampupModelAtomField),
    qiRampup: useAtomValue(gasForm.qiRampupAtomField ?? fallbackAtom),
    rampupDuration: useAtomValue(
      gasForm.rampupDurationAtomField ?? fallbackAtom
    ),
    n: useAtomValue(gasForm.nAtomField ?? fallbackAtom),
    ri: useAtomValue(gasForm.riAtomField ?? fallbackAtom),
    tElf: useAtomValue(gasForm.tElfAtomField ?? fallbackAtom),
    m: useAtomValue(gasForm.mAtomField ?? fallbackAtom),
    forecastDurationOption: useAtomValue(gasForm.forecastDurationOption),
  };

  const waterFormValues = {
    startDate: useAtomValue(waterForm.startDateAtomField),
    qi: useAtomValue(waterForm.qiAtomField),
    di: useAtomValue(waterForm.diAtomField),
    declineModel: useAtomValue(waterForm.declineModelAtomField),
    bFactor: useAtomValue(waterForm.bFactorAtomField ?? fallbackAtom),
    dlim: useAtomValue(waterForm.dlimAtomField ?? fallbackAtom),
    forecastDuration: useAtomValue(waterForm.forecastDuration),
    qiFormat: useAtomValue(waterForm.qiFormat),
    diFormat: useAtomValue(waterForm.diFormat),
    rampupModel: useAtomValue(waterForm.rampupModelAtomField),
    qiRampup: useAtomValue(waterForm.qiRampupAtomField ?? fallbackAtom),
    rampupDuration: useAtomValue(
      waterForm.rampupDurationAtomField ?? fallbackAtom
    ),
    n: useAtomValue(waterForm.nAtomField ?? fallbackAtom),
    ri: useAtomValue(waterForm.riAtomField ?? fallbackAtom),
    tElf: useAtomValue(waterForm.tElfAtomField ?? fallbackAtom),
    m: useAtomValue(waterForm.mAtomField ?? fallbackAtom),
    forecastDurationOption: useAtomValue(waterForm.forecastDurationOption),
  };

  // Helper function to create DCA params for a mineral type
  const createDcaParams = (formValues: typeof oilFormValues) => ({
    ...(formValues.dlim.value !== undefined && formValues.dlim.value !== null
      ? { dlim: formValues.dlim.value }
      : {}),
    ...(formValues.bFactor.value !== undefined &&
    formValues.bFactor.value !== null
      ? { bFactor: formValues.bFactor.value }
      : {}),
    dcaModel: formValues.declineModel.value,
    ...(formValues.declineModel.value !== 'ratio'
      ? {
          qi: formValues.qi.value ?? 0,
          di: formValues.di.value ?? 0,
          qiFormat: formValues.qiFormat.value,
        }
      : {}),
    forecastStartDate:
      parseMomentUtc(formValues.startDate.value).format(SERVER_DATE_FORMAT) ??
      '',
    durationPeriod: formValues.forecastDuration.value ?? 0,
    durationFormat: 'years' as const,
    ...(formValues.rampupModel.value !== 'none'
      ? {
          rampUp: {
            model: formValues.rampupModel.value,
            ...(formValues.qiRampup.value !== undefined &&
            formValues.qiRampup.value !== null
              ? { qi: formValues.qiRampup.value }
              : {}),
            ...(formValues.rampupDuration.value !== undefined &&
            formValues.rampupDuration.value !== null
              ? { t_ramp: formValues.rampupDuration.value }
              : {}),
            ...(formValues.n.value !== undefined && formValues.n.value !== null
              ? { n: formValues.n.value }
              : {}),
          },
        }
      : {}),
    ...(formValues.declineModel.value === 'ratio'
      ? {
          ri: formValues.ri.value ?? 0,
          tElf: formValues.tElf.value ?? 0,
          m: formValues.m.value ?? 0,
        }
      : {}),
  });

  const hasOilModelChanged = hasDeclineModelChanged(
    {
      bFactor: oilFormValues.bFactor.value,
      declineModel: oilFormValues.declineModel.value,
      di: oilFormValues.di.value,
      dlim: oilFormValues.dlim.value,
      forecastDuration: oilFormValues.forecastDuration.value,
      qi: oilFormValues.qi.value,
      qiFormat: oilFormValues.qiFormat.value,
      diFormat: oilFormValues.diFormat.value,
      startDate: oilFormValues.startDate.value,
      rampupModel: oilFormValues.rampupModel.value,
      qiRampup: oilFormValues.qiRampup.value,
      rampupDuration: oilFormValues.rampupDuration.value,
      n: oilFormValues.n.value,
      ri: oilFormValues.ri.value,
      tElf: oilFormValues.tElf.value,
      m: oilFormValues.m.value,
      forecastDurationOption: oilFormValues.forecastDurationOption.value,
    },
    prevModelFormValues.current.oil ?? undefined
  );

  const hasGasModelChanged = hasDeclineModelChanged(
    {
      bFactor: gasFormValues.bFactor.value,
      declineModel: gasFormValues.declineModel.value,
      di: gasFormValues.di.value,
      dlim: gasFormValues.dlim.value,
      forecastDuration: gasFormValues.forecastDuration.value,
      qi: gasFormValues.qi.value,
      qiFormat: gasFormValues.qiFormat.value,
      diFormat: gasFormValues.diFormat.value,
      startDate: gasFormValues.startDate.value,
      rampupModel: gasFormValues.rampupModel.value,
      qiRampup: gasFormValues.qiRampup.value,
      rampupDuration: gasFormValues.rampupDuration.value,
      n: gasFormValues.n.value,
      ri: gasFormValues.ri.value,
      tElf: gasFormValues.tElf.value,
      m: gasFormValues.m.value,
      forecastDurationOption: gasFormValues.forecastDurationOption.value,
    },
    prevModelFormValues.current.gas ?? undefined
  );
  const hasWaterModelChanged = hasDeclineModelChanged(
    {
      bFactor: waterFormValues.bFactor.value,
      declineModel: waterFormValues.declineModel.value,
      di: waterFormValues.di.value,
      dlim: waterFormValues.dlim.value,
      forecastDuration: waterFormValues.forecastDuration.value,
      qi: waterFormValues.qi.value,
      qiFormat: waterFormValues.qiFormat.value,
      diFormat: waterFormValues.diFormat.value,
      startDate: waterFormValues.startDate.value,
      rampupModel: waterFormValues.rampupModel.value,
      qiRampup: waterFormValues.qiRampup.value,
      rampupDuration: waterFormValues.rampupDuration.value,
      n: waterFormValues.n.value,
      ri: waterFormValues.ri.value,
      tElf: waterFormValues.tElf.value,
      m: waterFormValues.m.value,
      forecastDurationOption: waterFormValues.forecastDurationOption.value,
    },
    prevModelFormValues.current.water ?? undefined
  );

  const dcaForecast = useMemo(
    () => {
      const diFormat = ([
        [isOilModelValid, oilFormValues.diFormat.value],
        [isGasModelValid, gasFormValues.diFormat.value],
        [isWaterModelValid, waterFormValues.diFormat.value],
      ].find((item) => item[0]) ?? [true, 'effective' as const])[1] as DiFormat;

      const forecastDurationOption = ([
        [isOilModelValid, oilFormValues.forecastDurationOption.value],
        [isGasModelValid, gasFormValues.forecastDurationOption.value],
        [isWaterModelValid, waterFormValues.forecastDurationOption.value],
      ].find((item) => item[0]) ?? [
        true,
        'forecast_start' as const,
      ])[1] as ForecastDurationOption;

      return [
        {
          uwi: uwi,
          dcaParams: {
            diFormat: diFormat,
            forecastDurationOption: forecastDurationOption,
            ...(isOilModelValid
              ? {
                  oilDcaParams: {
                    ...createDcaParams(oilFormValues),
                    ...(oilFormValues.declineModel.value === 'ratio'
                      ? {
                          mainStream:
                            isGasModelValid &&
                            gasFormValues.declineModel.value !== 'ratio'
                              ? 'gas'
                              : 'oil',
                        }
                      : {}),
                  },
                }
              : {}),
            ...(isGasModelValid
              ? {
                  gasDcaParams: {
                    ...createDcaParams(gasFormValues),
                    ...(gasFormValues.declineModel.value === 'ratio'
                      ? {
                          mainStream:
                            isOilModelValid &&
                            oilFormValues.declineModel.value !== 'ratio'
                              ? 'oil'
                              : 'gas',
                        }
                      : {}),
                  },
                }
              : {}),
            ...(isWaterModelValid
              ? {
                  waterDcaParams: {
                    ...createDcaParams(waterFormValues),
                    ...(waterFormValues.declineModel.value === 'ratio'
                      ? {
                          mainStream:
                            isOilModelValid &&
                            oilFormValues.declineModel.value !== 'ratio'
                              ? 'oil'
                              : isGasModelValid &&
                                  gasFormValues.declineModel.value !== 'ratio'
                                ? 'gas'
                                : 'oil',
                        }
                      : {}),
                  },
                }
              : {}),
          },
        },
      ];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      uwi,
      isOilModelValid,
      isGasModelValid,
      isWaterModelValid,
      oilFormValues.diFormat.value,
      gasFormValues.diFormat.value,
      waterFormValues.diFormat.value,
      oilFormValues.forecastDurationOption.value,
      gasFormValues.forecastDurationOption.value,
      waterFormValues.forecastDurationOption.value,
      oilFormValues.startDate.value,
      oilFormValues.qi.value,
      oilFormValues.di.value,
      oilFormValues.bFactor.value,
      oilFormValues.dlim.value,
      oilFormValues.declineModel.value,
      oilFormValues.forecastDuration.value,
      oilFormValues.qiFormat.value,
      gasFormValues.startDate.value,
      gasFormValues.qi.value,
      gasFormValues.di.value,
      gasFormValues.bFactor.value,
      gasFormValues.dlim.value,
      gasFormValues.declineModel.value,
      gasFormValues.forecastDuration.value,
      gasFormValues.qiFormat.value,
      waterFormValues.startDate.value,
      waterFormValues.qi.value,
      waterFormValues.di.value,
      waterFormValues.bFactor.value,
      waterFormValues.dlim.value,
      waterFormValues.declineModel.value,
      waterFormValues.forecastDuration.value,
      waterFormValues.qiFormat.value,
      oilFormValues.rampupModel.value,
      oilFormValues.qiRampup.value,
      oilFormValues.rampupDuration.value,
      oilFormValues.n.value,
      gasFormValues.rampupModel.value,
      gasFormValues.qiRampup.value,
      gasFormValues.rampupDuration.value,
      gasFormValues.n.value,
      waterFormValues.rampupModel.value,
      waterFormValues.qiRampup.value,
      waterFormValues.rampupDuration.value,
      waterFormValues.n.value,
      oilFormValues.ri.value,
      oilFormValues.tElf.value,
      oilFormValues.m.value,
      gasFormValues.ri.value,
      gasFormValues.tElf.value,
      gasFormValues.m.value,
      waterFormValues.ri.value,
      waterFormValues.tElf.value,
      waterFormValues.m.value,
    ]
  );

  const isDcaForecastDirty =
    (isOilModelValid && hasOilModelChanged) ||
    (isGasModelValid && hasGasModelChanged) ||
    (isWaterModelValid && hasWaterModelChanged) ||
    regressionData.refetchEvalVolumes;

  // Clears the refetch flag once the forecast has gone dirty. The effect must
  // not re-run on its own write, so it reads the flag from `prev` rather than
  // depending on `regressionData`, and returns `prev` untouched when the flag
  // is already clear so no subscriber is notified.
  useEffect(() => {
    if (!isDcaForecastDirty) return;

    const timer = setTimeout(() => {
      setRegressionData((prev) => {
        if (!prev.refetchEvalVolumes) return prev;
        return { ...prev, refetchEvalVolumes: false };
      });
    }, 100);

    return () => clearTimeout(timer);
  }, [isDcaForecastDirty, setRegressionData]);

  useEffect(() => {
    if (isDcaForecastDirty) {
      setConvertToHyperbolicError([]);
      debouncedSetDeclineCurveAnalysisResponse({
        url: '/queries/reservoir/eval_volumes',
        type: 'POST',
        data: {
          query: {
            productionVolumes: [
              {
                uwis: [uwi],
                versionIds: [versionId],
              },
            ],
            pdpForecast: [
              {
                uwi: uwi,
                dcaForecast: dcaForecast,
              },
            ],
            responseConfig: {
              includeOnlyDcaForecast: true,
            },
          },
        },
      });
    }
  }, [
    setConvertToHyperbolicError,
    debouncedSetDeclineCurveAnalysisResponse,
    dcaForecast,
    versionId,
    isDcaForecastDirty,
    uwi,
  ]);

  useEffect(() => {
    prevModelFormValues.current.oil = {
      forecastDurationOption: oilFormValues.forecastDurationOption.value,
      bFactor: oilFormValues.bFactor.value,
      declineModel: oilFormValues.declineModel.value,
      di: oilFormValues.di.value,
      dlim: oilFormValues.dlim.value,
      forecastDuration: oilFormValues.forecastDuration.value,
      qi: oilFormValues.qi.value,
      qiFormat: oilFormValues.qiFormat.value,
      diFormat: oilFormValues.diFormat.value,
      startDate: oilFormValues.startDate.value,
      rampupModel: oilFormValues.rampupModel.value,
      qiRampup: oilFormValues.qiRampup.value,
      rampupDuration: oilFormValues.rampupDuration.value,
      n: oilFormValues.n.value,
      ri: oilFormValues.ri.value,
      tElf: oilFormValues.tElf.value,
      m: oilFormValues.m.value,
    };
    prevModelFormValues.current.gas = {
      forecastDurationOption: gasFormValues.forecastDurationOption.value,
      bFactor: gasFormValues.bFactor.value,
      declineModel: gasFormValues.declineModel.value,
      di: gasFormValues.di.value,
      dlim: gasFormValues.dlim.value,
      forecastDuration: gasFormValues.forecastDuration.value,
      qi: gasFormValues.qi.value,
      qiFormat: gasFormValues.qiFormat.value,
      diFormat: gasFormValues.diFormat.value,
      startDate: gasFormValues.startDate.value,
      rampupModel: gasFormValues.rampupModel.value,
      qiRampup: gasFormValues.qiRampup.value,
      rampupDuration: gasFormValues.rampupDuration.value,
      n: gasFormValues.n.value,
      ri: gasFormValues.ri.value,
      tElf: gasFormValues.tElf.value,
      m: gasFormValues.m.value,
    };
    prevModelFormValues.current.water = {
      forecastDurationOption: waterFormValues.forecastDurationOption.value,
      bFactor: waterFormValues.bFactor.value,
      declineModel: waterFormValues.declineModel.value,
      di: waterFormValues.di.value,
      dlim: waterFormValues.dlim.value,
      forecastDuration: waterFormValues.forecastDuration.value,
      qi: waterFormValues.qi.value,
      qiFormat: waterFormValues.qiFormat.value,
      diFormat: waterFormValues.diFormat.value,
      startDate: waterFormValues.startDate.value,
      rampupModel: waterFormValues.rampupModel.value,
      qiRampup: waterFormValues.qiRampup.value,
      rampupDuration: waterFormValues.rampupDuration.value,
      n: waterFormValues.n.value,
      ri: waterFormValues.ri.value,
      tElf: waterFormValues.tElf.value,
      m: waterFormValues.m.value,
    };
  });

  const { data, loading, error } = formatMutationResource(
    declineCurveAnalysisResponse
  );

  const resetDeclineCurveAnalysis = useCallback(() => {
    setResetKey((prev) => prev + 1);
  }, []);

  return {
    data,
    loading,
    error,
    dcaForecast,
    isDcaForecastDirty,
    resetDeclineCurveAnalysis,
  };
};

export const useConvertToHyperbolic = (
  uwi: string,
  versionId: string,
  streamType: MineralType
) => {
  const modelFormAtoms = {
    oil: {
      modelFormValuesAtom: oilModelFormValuesAtom,
      modelFormAtom: oilModelFormAtom,
    },
    gas: {
      modelFormValuesAtom: gasModelFormValuesAtom,
      modelFormAtom: gasModelFormAtom,
    },
    water: {
      modelFormValuesAtom: waterModelFormValuesAtom,
      modelFormAtom: waterModelFormAtom,
    },
  };
  const ratioModelFormValuesAtom =
    modelFormAtoms[streamType].modelFormValuesAtom;
  const ratioDCAModelFormValues = useAtomValue(ratioModelFormValuesAtom);

  const ratioDCAModelFormAtom = modelFormAtoms[streamType].modelFormAtom;
  const setRatioDCAModelForm = useSetAtom(ratioDCAModelFormAtom);

  const [isConvertingToHyperbolic, setIsConvertingToHyperbolic] =
    useState(false);
  const primaryDCAModel = useAtomValue(primaryDCAModelAtom);
  const convertToHyperbolicAtom = useMemo(() => {
    return createMutationAtom<DeclineCurveAnalysis, 'declineCurveAnalysis'>();
  }, []);

  const [convertToHyperbolicResponse, setConvertToHyperbolicResponse] = useAtom(
    convertToHyperbolicAtom
  );
  const setConvertToHyperbolicError = useSetAtom(convertToHyperbolicErrorAtom);

  useEffect(() => {
    if (!isNil(primaryDCAModel) && isConvertingToHyperbolic) {
      unstable_batchedUpdates(() => {
        setConvertToHyperbolicError([]);
        const diFormat = primaryDCAModel.formValues.diFormat.value;
        const forecastDurationOption =
          primaryDCAModel.formValues.forecastDurationOption.value;
        setConvertToHyperbolicResponse({
          url: '/queries/reservoir/eval_volumes',
          type: 'POST',
          data: {
            query: {
              pdpForecast: [
                {
                  uwi: uwi,
                  dcaForecast: [
                    {
                      uwi: uwi,
                      dcaParams: {
                        diFormat: diFormat,
                        forecastDurationOption: forecastDurationOption,
                        [`${streamType}DcaParams`]: {
                          dcaModel: ratioDCAModelFormValues.declineModel.value,
                          ri: ratioDCAModelFormValues.ri?.value,
                          tElf: ratioDCAModelFormValues.tElf?.value,
                          m: ratioDCAModelFormValues.m?.value,
                          forecastStartDate: parseMomentUtc(
                            ratioDCAModelFormValues.startDate.value
                          ).format(SERVER_DATE_FORMAT),
                          durationPeriod:
                            ratioDCAModelFormValues.forecastDuration.value,
                          durationFormat: 'years' as const,
                          mainStream: primaryDCAModel.streamType,
                          convertToDcaParameters: true,
                        },
                        [`${primaryDCAModel.streamType}DcaParams`]: {
                          dcaModel:
                            primaryDCAModel.formValues.declineModel.value,
                          qi: primaryDCAModel.formValues.qi.value,
                          di: primaryDCAModel.formValues.di.value,
                          bFactor: primaryDCAModel.formValues.bFactor?.value,
                          dlim: primaryDCAModel.formValues.dlim?.value,
                          forecastStartDate: parseMomentUtc(
                            primaryDCAModel.formValues.startDate.value
                          ).format(SERVER_DATE_FORMAT),
                          durationPeriod:
                            primaryDCAModel.formValues.forecastDuration.value,
                          durationFormat: 'years' as const,
                          qiFormat: primaryDCAModel.formValues.qiFormat.value,
                          ...(primaryDCAModel.formValues.rampupModel.value !==
                          'none'
                            ? {
                                rampUp: {
                                  model:
                                    primaryDCAModel.formValues.rampupModel
                                      .value,
                                  qi: primaryDCAModel.formValues.qiRampup
                                    ?.value,
                                  t_ramp:
                                    primaryDCAModel.formValues.rampupDuration
                                      ?.value,
                                  n: primaryDCAModel.formValues.n?.value,
                                },
                              }
                            : {}),
                        },
                      },
                    },
                  ],
                },
              ],
              productionVolumes: [
                {
                  uwis: [uwi],
                  versionIds: [versionId],
                },
              ],
              responseConfig: {
                includeOnlyDcaForecast: true,
              },
            },
          },
          onSuccess: (data) => {
            unstable_batchedUpdates(() => {
              setIsConvertingToHyperbolic(false);
              const responsePayload = data.pdpDeclines[uwi][0];
              const convertedDcaParameters =
                responsePayload.convertedDcaParameters?.[
                  `${streamType}DcaParams`
                ];

              if (
                convertedDcaParameters &&
                responsePayload.errors.length === 0
              ) {
                setRatioDCAModelForm({
                  rampupModel: 'none',
                  declineModel: primaryDCAModel.formValues.declineModel.value,
                  forecastStartDate: parseMomentUtc(
                    convertedDcaParameters.forecastStartDate
                  ).toDate(),
                  qi: convertedDcaParameters.qi,
                  di: Math.round(convertedDcaParameters.di * 100) / 100,
                  bFactor:
                    Math.round(convertedDcaParameters.bFactor * 100) / 100,
                  dlim: Math.round(convertedDcaParameters.dlim * 100) / 100,
                  qiRampup: null,
                  rampupDuration: null,
                  n: null,
                  ri: null,
                  tElf: null,
                  m: null,
                });
              } else if (responsePayload.errors.length > 0) {
                setConvertToHyperbolicError(responsePayload.errors);
                setIsConvertingToHyperbolic(false);
              }
            });
          },
          onError: (error: any) => {
            unstable_batchedUpdates(() => {
              setConvertToHyperbolicError(
                error ?? [
                  {
                    code: 'UNKNOWN_ERROR',
                    message: 'Internal server error',
                    details: { streamType: streamType, field: 'unknown' },
                  },
                ]
              );
              setIsConvertingToHyperbolic(false);
            });
          },
        });
      });
    }
  }, [
    primaryDCAModel,
    ratioDCAModelFormValues,
    isConvertingToHyperbolic,
    uwi,
    versionId,
    setConvertToHyperbolicError,
    setConvertToHyperbolicResponse,
    setRatioDCAModelForm,
    streamType,
  ]);

  return {
    data: formatMutationResource(convertToHyperbolicResponse),
    setIsConvertingToHyperbolic,
  };
};

export const useUnitDetailsAtom = (uwi: string, versionId?: string) => {
  return useMemo(() => {
    if (!uwi) {
      return atom({ loading: false, data: undefined });
    }
    if (versionId === 'current' || !versionId) {
      return createResourceAtom<BestFitUnitResponse>(
        `/wells/${uwi}/best_fit_unit`
      );
    }
    // For other versions, include the version timestamp in the URL
    return createResourceAtom<BestFitUnitResponse>(
      `/wells/${uwi}/versions/${versionId}/best_fit_unit`
    );
  }, [uwi, versionId]);
};

export const useWellIntersectionAnalysis = (
  uwi: string,
  versionTimestamp?: string,
  /**
   * The Well Workspace this card is being read inside, if any. The backend resolves the
   * as-of from it (`WorkspaceResolver`), exactly as the permit and stimulation queries
   * do — without it a promoted workspace's Unit Details shows live intersection data
   * instead of the workspace's own. Omitted on the Well page, which reads live.
   */
  workspaceId?: number
) => {
  const wellIntersectionAnalysisAtom = useMemo(() => {
    return createMutationAtom<
      WellIntersectionAnalysisResponse,
      'wellIntersectionAnalysis'
    >();
  }, []);

  const [response, setResponse] = useAtom(wellIntersectionAnalysisAtom);

  useEffect(() => {
    if (!uwi) return;

    setResponse({
      url: '/queries/well_intersection_analysis',
      type: 'POST',
      data: {
        query: {
          well_uwi: uwi,
          ...(versionTimestamp && versionTimestamp !== 'current'
            ? { version_timestamp: versionTimestamp }
            : {}),
          // snake_case, nested in `query`: this controller does not underscore incoming
          // keys, so a camelCase `workspaceId` is dropped by `permit` without erroring.
          ...(workspaceId ? { workspace_id: workspaceId } : {}),
        },
      },
    });
  }, [uwi, versionTimestamp, workspaceId, setResponse]);

  const { data, loading, error } = formatMutationResource(response);

  return { data, loading, error };
};

export const useDcaScenarios = (): {
  dcaScenarios: Array<DcaScenariosResponse & { label: string }>;
  isDcaScenariosLoading: boolean;
  dcaScenariosError: string | undefined;
  setDcaScenariosResponse: (
    payload:
      | ((params: SearchParams | undefined) => SearchParams | undefined)
      | undefined
  ) => void;
} => {
  const { searchAtom: dcaScenariosAtom } = useMemo(() => {
    return searchAtomPair<DcaScenariosResponse, SearchParams>({
      href: '/queries/reservoir/dca_scenarios',
    });
  }, []);

  const [dcaScenariosResponse, setDcaScenariosResponse] =
    useAtom(dcaScenariosAtom);

  useEffect(() => {
    setDcaScenariosResponse(() => ({
      page: 1,
      pageSize: 100,
      sort_attribute: 'created_at',
      sort_direction: 'DESC',
    }));
  }, [setDcaScenariosResponse]);

  const dcaScenarios = useMemo((): Array<
    DcaScenariosResponse & { label: string }
  > => {
    if (!dcaScenariosResponse.data?.results) return [];
    return dcaScenariosResponse.data.results.map((scenario) => ({
      ...scenario,
      label: scenario.name,
    }));
  }, [dcaScenariosResponse.data]);

  return {
    dcaScenarios,
    isDcaScenariosLoading: dcaScenariosResponse.loading ?? false,
    dcaScenariosError: dcaScenariosResponse.error,
    setDcaScenariosResponse,
  };
};

export const useDcaVolumeHotkeys = ({
  formAtom,
  scope,
}: {
  formAtom: VolumeModelFormAtoms;
  scope: keyof typeof HOTKEY_CONFIG;
}) => {
  const form = useAtomValue(formAtom);
  // Hotkeys mode picks which set of parameter keys applies, Insights or ARIES.
  const { mapping } = useAtomValue(dcaHotkeysModeAtom);
  const keyConfig = HOTKEY_CONFIG_BY_MAPPING[mapping];

  const rampupModel = useAtomValue(form.rampupModelAtomField);
  const declineModel = useAtomValue(form.declineModelAtomField);
  const diFormat = useAtomValue(form.diFormat);

  const getLimit = (paramName: string, isRampup: boolean = false) => {
    return getValidationRange(
      paramName,
      isRampup ? 'ramp_up' : 'biased',
      isRampup && rampupModel.value !== 'none'
        ? rampupModel.value
        : declineModel.value,
      'absolute',
      diFormat.value
    );
  };

  useDcaVolumeHotkey(
    form.qiAtomField,
    keyConfig[scope].qi.incrementKey,
    keyConfig[scope].qi.decrementKey,
    keyConfig[scope].qi.increment,
    keyConfig[scope].qi.decrement,
    scope,
    getLimit('qi')
  );

  useDcaVolumeHotkey(
    form.riAtomField,
    keyConfig[scope].ri.incrementKey,
    keyConfig[scope].ri.decrementKey,
    keyConfig[scope].ri.increment,
    keyConfig[scope].ri.decrement,
    scope,
    getLimit('ri')
  );

  useDcaVolumeHotkey(
    form.diAtomField,
    keyConfig[scope].di.incrementKey,
    keyConfig[scope].di.decrementKey,
    keyConfig[scope].di.increment,
    keyConfig[scope].di.decrement,
    scope,
    getLimit('di')
  );

  useDcaVolumeHotkey(
    form.bFactorAtomField,
    keyConfig[scope].bFactor.incrementKey,
    keyConfig[scope].bFactor.decrementKey,
    keyConfig[scope].bFactor.increment,
    keyConfig[scope].bFactor.decrement,
    scope,
    getLimit('bFactor')
  );

  useDcaVolumeHotkey(
    form.mAtomField,
    keyConfig[scope].m.incrementKey,
    keyConfig[scope].m.decrementKey,
    keyConfig[scope].m.increment,
    keyConfig[scope].m.decrement,
    scope,
    getLimit('m')
  );

  useDcaVolumeHotkey(
    form.dlimAtomField,
    keyConfig[scope].dlim.incrementKey,
    keyConfig[scope].dlim.decrementKey,
    keyConfig[scope].dlim.increment,
    keyConfig[scope].dlim.decrement,
    scope,
    getLimit('dlim')
  );

  useDcaVolumeHotkey(
    form.nAtomField,
    keyConfig[scope].rampupN.incrementKey,
    keyConfig[scope].rampupN.decrementKey,
    keyConfig[scope].rampupN.increment,
    keyConfig[scope].rampupN.decrement,
    scope,
    getLimit('n', true)
  );

  useDcaVolumeHotkey(
    form.rampupDurationAtomField,
    keyConfig[scope].rampupDuration.incrementKey,
    keyConfig[scope].rampupDuration.decrementKey,
    keyConfig[scope].rampupDuration.increment,
    keyConfig[scope].rampupDuration.decrement,
    scope,
    [0, 100000]
  );

  useDcaVolumeHotkey(
    form.tElfAtomField,
    keyConfig[scope].tElf.incrementKey,
    keyConfig[scope].tElf.decrementKey,
    keyConfig[scope].tElf.increment,
    keyConfig[scope].tElf.decrement,
    scope,
    getLimit('tElf')
  );

  useDcaVolumeHotkey(
    form.qiRampupAtomField,
    keyConfig[scope].rampupQi.incrementKey,
    keyConfig[scope].rampupQi.decrementKey,
    keyConfig[scope].rampupQi.increment,
    keyConfig[scope].rampupQi.decrement,
    scope,
    getLimit('qi', true)
  );
};

// Stable module-scope fallback for the optional atomField. An inline
// `atom(null)` in render creates a fresh atom every render, which React 18's
// jotai binding turns into an infinite re-render loop.
const nullFallbackAtom = atom(null);

export const useDcaVolumeHotkey = (
  atomField: AtomField<number | null> | undefined,
  incrementHotkeys: Array<string>,
  decrementHotkeys: Array<string>,
  increment: (number: number | null, multiplier: number) => number,
  decrement: (number: number | null, multiplier: number) => number,
  scope: keyof typeof HOTKEY_CONFIG,
  limits: [number, number]
) => {
  const setField = useSetAtom(atomField ?? nullFallbackAtom);
  const { activeScopes } = useHotkeysContext();
  const { active: modeActive } = useAtomValue(dcaHotkeysModeAtom);
  // A mapping that binds no key to this parameter passes empty key lists, and
  // ARIES does that for most of them. Register nothing in that case.
  const keys = [...incrementHotkeys, ...decrementHotkeys];
  const incremenetMultiplierRef = useRef<number>(1);
  const decrementMultiplierRef = useRef<number>(1);

  const createMultiplier = (ref: { current: number }) => {
    let resetId: number;
    return () => {
      clearTimeout(resetId);
      ref.current = ref.current * 2;
      resetId = window.setTimeout(() => {
        ref.current = 1;
      }, 1000);
    };
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const incrementMultiplier = useCallback(
    createMultiplier(incremenetMultiplierRef),
    []
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const decrementMultiplier = useCallback(
    createMultiplier(decrementMultiplierRef),
    []
  );

  const hotkeyOptions = {
    scopes: scope,
    // Parameter keys work only while hotkeys mode is on, so typing in the
    // workspace cannot change a forecast by accident.
    enabled: modeActive && activeScopes.includes(scope) && keys.length > 0,
    enableOnFormTags: true,
    preventDefault: true,
  };

  const createHotkeyHandler =
    (
      operation: (prev: number | null, multiplier: number) => number,
      multiplierRef: { current: number },
      updateMultiplier: () => void
    ) =>
    () => {
      unstable_batchedUpdates(() => {
        setField((prev: number | null) => {
          const newValue = operation(prev, multiplierRef.current);
          const [minBound, maxBound] = limits;
          // Clamp to dynamic bounds
          if (newValue < minBound) {
            return minBound; // Clamp to lower bound
          }
          if (newValue > maxBound) {
            return maxBound; // Clamp to upper bound
          }

          return newValue;
        });
        updateMultiplier();
      });
    };

  useInsightsHotkeys(
    incrementHotkeys,
    createHotkeyHandler(
      increment,
      incremenetMultiplierRef,
      incrementMultiplier
    ),
    hotkeyOptions
  );

  useInsightsHotkeys(
    decrementHotkeys,
    createHotkeyHandler(decrement, decrementMultiplierRef, decrementMultiplier),
    hotkeyOptions
  );
};

export const useDcaStreamNavigation = () => {
  const { disableScope, toggleScope, activeScopes } = useHotkeysContext();
  const handleStreamNavigation = useCallback(
    (direction: 'next' | 'prev') => {
      const currentActiveStream = STREAM_ORDER.find((scope) =>
        activeScopes.includes(scope)
      );
      if (!currentActiveStream) return;
      const currentIndex = STREAM_ORDER.indexOf(currentActiveStream);
      let nextIndex: number;
      if (direction === 'next') {
        nextIndex = (currentIndex + 1) % STREAM_ORDER.length;
      } else {
        nextIndex =
          (currentIndex - 1 + STREAM_ORDER.length) % STREAM_ORDER.length;
      }
      const nextStream = STREAM_ORDER[nextIndex];
      disableScope(currentActiveStream);
      toggleScope(nextStream);
    },
    [activeScopes, disableScope, toggleScope]
  );

  const streamNavOptions = {
    enabled: activeScopes.some((scope) =>
      STREAM_ORDER.includes(scope as HOTKEY_SCOPES)
    ),
    enableOnFormTags: true,
  };

  useHotkeys(
    ['tab', 'shift+down'],
    (event) => {
      event.preventDefault();
      handleStreamNavigation('next');
    },
    streamNavOptions,
    [handleStreamNavigation, activeScopes]
  );

  useHotkeys(
    ['shift+tab', 'shift+up'],
    (event) => {
      event.preventDefault();
      handleStreamNavigation('prev');
    },
    streamNavOptions,
    [handleStreamNavigation, activeScopes]
  );
  return { handleStreamNavigation };
};

type SourceOverride = {
  resultHref: string | null;
  displayName: string | null;
  instanceId: number | null;
  // Tracks which instance was active when "Current Config" was selected.
  // When a new LA result arrives with a different id, the override is removed
  // and the row updates automatically.
  clearedInstanceId?: number | null;
  // True when the user chose "Current Config" and an existing LA result was
  // already present. The row should keep showing that result (not go blank).
  isCurrentConfig?: boolean;
  // Persists the last fresh LA href across pinned-instance overrides so
  // "Current Config" can restore it even after the preview cache is reset.
  laHref?: string | null;
  // True when this override was auto-created from a default instance on the
  // initial page visit (not an explicit pin via the Edit Source modal). These
  // rows must NOT be treated as user-pinned, so they are re-run on Apply of
  // Landing Analysis Settings.
  isDefaultInit?: boolean;
};

export const useWellLandingAnalysisMutation = () => {
  const [mutationState, dispatchLandingAnalysis] = useAtom(
    wellLandingAnalysisMutationAtom
  );
  const { loading: isMutating } = formatMutationResource(mutationState);
  const dispatchEditSourceRefresh = useSetAtom(triggerEditSourceRefreshAtom);

  const [sourceOverrides, setSourceOverrides] = useState<
    Record<string, SourceOverride>
  >({});
  const [editSource, setEditSource] = useState<
    { href: string; uwi14: string } | undefined
  >();

  const editSourceAtom = useWellLandingAnalysisPreviewAtom(editSource?.href);
  const editSourceResult = useAtomValue(editSourceAtom);
  const editSourceUwi14 = editSource?.uwi14;

  const saveEditSource = useCallback(
    (
      instance: WellLandingAnalysisInstance,
      uwi14: string,
      onSuccess?: () => void
    ) => {
      const displayName = parseUTCdateTime(instance.createdAt);
      dispatchEditSourceRefresh();
      setEditSource({ href: instance.href, uwi14 });
      setSourceOverrides((prev) => {
        const prev0 = prev[uwi14];
        // Carry forward the last fresh LA href so "Current Config" can still
        // restore it even after pinning to a specific saved instance.
        const laHref = prev0?.isCurrentConfig
          ? prev0.resultHref
          : prev0?.laHref;
        return {
          ...prev,
          [uwi14]: {
            resultHref: instance.href,
            displayName,
            instanceId: instance.id,
            laHref: laHref ?? null,
          },
        };
      });
      onSuccess?.();
    },
    [dispatchEditSourceRefresh]
  );

  const resetToOriginalSource = useCallback(
    (originalHref: string, uwi14: string, onSuccess?: () => void) => {
      dispatchEditSourceRefresh();
      setEditSource({ href: originalHref, uwi14 });
      // instanceId=null marks "Current Config" selected. isCurrentConfig=true
      // keeps the row showing existing data while the API call is in flight.
      setSourceOverrides((prev) => ({
        ...prev,
        [uwi14]: {
          resultHref: originalHref,
          displayName: null,
          instanceId: null,
          isCurrentConfig: true,
        },
      }));
      onSuccess?.();
    },
    [dispatchEditSourceRefresh]
  );

  // Used when "Current Config" is selected for a row with no fresh LA result.
  // Sets instanceId=null so the row shows blank and initializeSourceOverride
  // won't re-apply the default. clearedInstanceId allows the override to be
  // removed automatically once a new LA result arrives.
  const clearSourceOverride = useCallback(
    (
      uwi14: string,
      clearedInstanceId: number | null | undefined,
      onSuccess?: () => void
    ) => {
      setSourceOverrides((prev) => ({
        ...prev,
        [uwi14]: {
          resultHref: null,
          displayName: null,
          instanceId: null,
          clearedInstanceId: clearedInstanceId ?? null,
          isCurrentConfig: false,
        },
      }));
      onSuccess?.();
    },
    []
  );

  // Transitions a "Current Config" override to isCurrentConfig:true once a new
  // LA result arrives. Stores the result href so onLoad(null) can restore it even
  // after a cache reset overwrites allLandingAnalysisResults for this uwi14.
  const removeSourceOverride = useCallback(
    (uwi14: string, resultHref?: string) => {
      setSourceOverrides((prev) => ({
        ...prev,
        [uwi14]: {
          instanceId: null,
          resultHref: resultHref ?? null,
          displayName: null,
          isCurrentConfig: true,
        },
      }));
    },
    []
  );

  // Sets a source override without triggering a new fetch — used to pre-populate
  // the display name when a default instance is auto-loaded on initial page visit.
  // Skips uwi14s that already have any override (including user-cleared ones) so
  // a later removeSourceOverride can never re-apply the default.
  const initializeSourceOverride = useCallback(
    (instance: WellLandingAnalysisInstance, uwi14: string) => {
      setSourceOverrides((prev) => {
        if (uwi14 in prev) return prev;
        return {
          ...prev,
          [uwi14]: {
            resultHref: instance.href,
            displayName: parseUTCdateTime(instance.createdAt),
            instanceId: instance.id,
            isDefaultInit: true,
          },
        };
      });
    },
    []
  );

  // Re-point an auto-init (default-init) override at a different instance, so a
  // pre-drill subject's auto-loaded Source follows the selected workspace. Manual
  // Edit Source picks and already-matching overrides are left untouched.
  const syncDefaultSourceOverride = useCallback(
    (instance: WellLandingAnalysisInstance, uwi14: string) => {
      setSourceOverrides((prev) => {
        const existing = prev[uwi14];
        if (existing && !existing.isDefaultInit) return prev;
        if (existing?.instanceId === instance.id) return prev;
        return {
          ...prev,
          [uwi14]: {
            resultHref: instance.href,
            displayName: parseUTCdateTime(instance.createdAt),
            instanceId: instance.id,
            isDefaultInit: true,
          },
        };
      });
    },
    []
  );

  return {
    isMutating,
    dispatchLandingAnalysis,
    sourceOverrides,
    editSourceUwi14,
    editSourceResult,
    saveEditSource,
    resetToOriginalSource,
    clearSourceOverride,
    removeSourceOverride,
    initializeSourceOverride,
    syncDefaultSourceOverride,
  };
};

export const useLandingAnalysisBanner = (
  params: UseLandingAnalysisBannerParams
): LandingAnalysisBannerResult => {
  const {
    isSelectedCollectionInvalid,
    sgCollectionsLoaded,
    structureGridCollectionsCount,
    individualGridsCount,
    wellLandingAnalysisLoading,
    offsetWellLandingAnalysisLoading,
    previewCacheHasLoading,
    allLandingAnalysisResults,
    wellboreUwi14s,
    savedSubjectUwi14s,
    selectedStructureGridIdsCount,
  } = params;

  const analysisResultCount = Object.keys(allLandingAnalysisResults).length;

  const isAnalysisInFlight =
    wellLandingAnalysisLoading ||
    offsetWellLandingAnalysisLoading ||
    previewCacheHasLoading;

  const hasNoStructureGridInfo =
    sgCollectionsLoaded &&
    structureGridCollectionsCount === 0 &&
    individualGridsCount === 0;

  const hasNewWells =
    !isAnalysisInFlight &&
    analysisResultCount > 0 &&
    wellboreUwi14s.some(
      (uwi14) =>
        !(uwi14 in allLandingAnalysisResults) &&
        !savedSubjectUwi14s.includes(uwi14)
    );

  const hasIndividualGridsSelected =
    !isAnalysisInFlight &&
    analysisResultCount > 0 &&
    selectedStructureGridIdsCount > 0;

  const subjectBanners: LandingBannerType[] = [];
  if (isSelectedCollectionInvalid) subjectBanners.push('invalidCollection');
  if (hasNoStructureGridInfo) subjectBanners.push('noStructureGrids');
  if (hasIndividualGridsSelected) subjectBanners.push('individualGrids');

  const offsetBanners: LandingBannerType[] = [];
  if (hasNewWells) offsetBanners.push('newWells');

  return { subjectBanners, offsetBanners, isAnalysisInFlight };
};

const STIMULATIONS_SEARCH_DEBOUNCE_MS = 300;

const STIMULATION_DATE_COLUMNS = new Set([
  'jobReportedEndDate',
  'jobDate',
  'jobEndDate',
  'jobStartDate',
  'vendorDateCreated',
  'vendorUpdatedDate',
  'createdAt',
  'updatedAt',
]);

function toDateRange(
  value: unknown
): { start?: string; end?: string } | undefined {
  if (!Array.isArray(value) || value.length !== 2) return undefined;
  const [start, end] = value as [string | null, string | null];
  if (!start && !end) return undefined;
  const o: { start?: string; end?: string } = {};
  if (start) o.start = start;
  if (end) o.end = end;
  return o;
}

function toMinMax(value: unknown): { min?: number; max?: number } | undefined {
  if (!Array.isArray(value) || value.length !== 2) return undefined;
  const [min, max] = value as [number | null, number | null];
  if (min === null && max === null) return undefined;
  const o: { min?: number; max?: number } = {};
  if (min !== null) o.min = min;
  if (max !== null) o.max = max;
  return o;
}

function buildStimulationFilters(
  columnFilters: ColumnFiltersState
): Record<string, unknown> | undefined {
  const filters: Record<string, unknown> = {};

  for (const { id, value } of columnFilters) {
    if (value === undefined || value === null || value === '') continue;

    if (STIMULATION_DATE_COLUMNS.has(id)) {
      const r = toDateRange(value);
      if (r) filters[id] = r;
    } else if (
      Array.isArray(value) &&
      value.length === 2 &&
      value.every((v) => v === null || typeof v === 'number')
    ) {
      const r = toMinMax(value);
      if (r) filters[id] = r;
    } else if (Array.isArray(value)) {
      if (value.length > 0) filters[id] = value;
    } else if (typeof value === 'string' && value.trim()) {
      filters[id] = value.trim();
    }
  }

  return Object.keys(filters).length > 0 ? filters : undefined;
}

export type SourceWellSimulationsHook = ReturnType<
  typeof useSourceWellSimulations
>;

export const useSourceWellSimulations = (
  uwi: string,
  resourceHref: string,
  // Resolves the rows as-of a workspace; omitted on the Well page, which reads live.
  workspaceId?: number
) => {
  const [data, setData] = useState<StimulationRow[]>([]);
  const [meta, setMeta] = useState<{ total: number }>({ total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy, setSortBy] = useState<string>('jobDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const t = setTimeout(
      () => setDebouncedSearch(globalFilter),
      STIMULATIONS_SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(t);
  }, [globalFilter]);

  const fetchSimulations = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const query: Record<string, unknown> = {
        uwi,
        page,
        pageSize,
        sortAttribute: snakeCase(sortBy),
        sortDirection: sortOrder,
      };

      // snake_case, and inside the `query` object — this endpoint permits off
      // `params.accept(:query)` and, unlike the wells controller, does not underscore
      // incoming keys, so a camelCase key is dropped by strong params without an error.
      // A stim read carrying one routes to the PG as-of path, same response shape.
      if (workspaceId) query.workspace_id = workspaceId;

      const filters = buildStimulationFilters(columnFilters);
      if (filters) query.filters = filters;
      if (debouncedSearch) query.text = debouncedSearch;

      const response = await request.post<
        StimulationResponse,
        FailedApiResponse
      >(resourceHref, { query }, controller);

      if (controller.signal.aborted) return;

      if (response.type === 'error') {
        const error = response.data?.errors[0];
        const { details } = error || {};
        let msg = error?.message || 'Failed to load stimulations';
        if (details && typeof details === 'object') {
          msg = Object.entries(details)
            .map(([key, value]) => `${key}: ${value}`)
            .join('\n');
        }
        throw new Error(msg);
      } else if (response.type !== 'success') {
        throw new Error(response.message || 'Failed to load stimulations');
      }

      setData(response.data.results);
      setMeta({ total: response.data.totalCount });
    } catch (err) {
      if (!controller.signal.aborted) {
        setError(
          err instanceof Error ? err.message : 'Failed to load stimulations'
        );
        setData([]);
        setMeta({ total: 0 });
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [
    uwi,
    resourceHref,
    workspaceId,
    page,
    pageSize,
    debouncedSearch,
    sortBy,
    sortOrder,
    columnFilters,
  ]);

  useEffect(() => {
    if (!uwi) return;
    void fetchSimulations();
    return () => {
      abortRef.current?.abort();
    };
  }, [fetchSimulations, uwi]);

  const onFilterChange = useCallback((filters: ColumnFiltersState) => {
    setColumnFilters(filters);
    setPage(1);
  }, []);

  const onGlobalSearchChange = useCallback((search: string) => {
    setGlobalFilter(search);
    setPage(1);
  }, []);

  const onPageChange = useCallback((p: number, ps: number) => {
    setPage(p);
    setPageSize(ps);
  }, []);

  const onSortChange = useCallback(
    (sorting: { id: string; desc: boolean }[]) => {
      if (sorting.length > 0) {
        setSortBy(sorting[0].id);
        setSortOrder(sorting[0].desc ? 'desc' : 'asc');
      }
      setPage(1);
    },
    []
  );

  return {
    data,
    meta,
    loading,
    error,
    page,
    pageSize,
    onFilterChange,
    onGlobalSearchChange,
    onPageChange,
    onSortChange,
    fetchSimulations,
  };
};
